import React from "react";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Article, addDaySuffix } from "@ecologi/shared";
import { Link } from "../Link";
import { getResizedImageURL } from "../utils";

import { theme } from "../theme";

type Props = {
  article: Article;
  isGrey?: boolean;
};

const ListItem = styled.li`
  flex: 0 0 100%;
  display: flex;
  padding: 0 10px;

  @media ${theme.bp.tabletUp} {
    flex: 0 0 50%;
  }

  @media ${theme.bp.desktopUp} {
    flex: 0 0 33.333%;
  }
`;

const Image = styled.img<{ isImageless: boolean }>`
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  height: 190px;
  object-fit: cover;
  width: 100%;

  ${(props) =>
    props.isImageless &&
    css`
      background-image: linear-gradient(
        45deg,
        #0d755d 25%,
        #008b6a 25%,
        #008b6a 50%,
        #0d755d 50% #0d755d 75%,
        #008b6a 75%,
        #008b6a 100
      );
      background-size: 56.57px 56.57px;
    `}
`;

const ListItemLink = styled(Link)<{ isGrey?: boolean }>`
  background: ${(props) =>
    props.isGrey ? theme.colors.mist : theme.colors.snowdrop};
  display: block;
  flex: 1;
  margin: 0 0 24px;
  text-decoration: none;

  &:hover h4 {
    text-decoration: underline;
  }
`;

const Info = styled.div`
  color: ${theme.colors.charcoal};
  background-color: ${theme.colors.snowdrop};
  height: 135px;
  padding: 20px 20px 60px;
`;

const Time = styled.div<{ dateTime: string }>`
  display: block;
  font-size: 14px;
  margin-bottom: 4px;
  opacity: 0.8;
`;

const Title = styled.h4`
  font-size: 18px;
  font-weight: bold;
`;

export const ArticleListItem: React.FC<Props> = ({ article, isGrey }) => {
  const imageUrl = article.acf.images ? article.acf.images[0].sizes.large : "";
  const optimisedImageUrl = imageUrl ? getResizedImageURL(imageUrl, 600) : null;
  const date = DateTime.fromISO(article.date_gmt);
  const day = date.toFormat("d");
  const formattedDate = `${addDaySuffix(day)} ${date.toFormat("MMM y")}`;

  const type =
    article.type === "post"
      ? "articles/"
      : article.type === "project"
      ? "projects/"
      : "";
  let category =
    article._embedded &&
    article._embedded["wp:term"] &&
    article._embedded["wp:term"][0][0].slug;
  category = category ? category + "/" : "";

  return (
    <ListItem key={article.id}>
      <ListItemLink
        href={`/${type}${category}${article.slug}`}
        target="_blank"
        rel="noopener"
        title={article.title.rendered}
        isGrey={isGrey}
      >
        <Image
          isImageless={!optimisedImageUrl}
          loading="lazy"
          src={optimisedImageUrl || ""}
        />
        <Info>
          <Time dateTime={article.date_gmt}>{formattedDate}</Time>
          <Title>{article.title.rendered}</Title>
        </Info>
      </ListItemLink>
    </ListItem>
  );
};
